import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  FormContainer,
  FormContent,
} from '@/shared/components/ui/form/form.styled';
import { TFormWizardFormProps } from '@/shared/components/ui/form-wizard/components/form-wizard-form/form-wizard-form.types';
import { FormWizardSubmit } from '@/shared/components/ui/form-wizard/components/form-wizard-submit/form-wizard-submit';
import { useFormWizard } from '@/shared/components/ui/form-wizard/context/form-wizard-context';
import { StepTag } from '@/shared/components/ui/tag/variant/step-tag/step-tag';

export const FormWizardForm: React.FC<TFormWizardFormProps> = ({
  handleClose,
  children,
  title,
  maxSteps,
  overflowVisible,
  skipable,
  skipSteps = 1,
  submitButtonLabel,
  formHasToBeDirty,
}) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext();
  const {
    stepsData,
    setStepsData,
    lastFormIndex,
    setLastFormIndex,
    step,
    setStep,
  } = useFormWizard();
  const closeButtonLabel = step > 0 ? t('common:back') : t('common:cancel');
  const submitButtonOnly = !handleClose && step === 0;

  const _handleClose = () => {
    if (step > 0) {
      const currentValues = getValues();
      setStepsData(
        Object.assign([...stepsData], {
          [step]: { data: currentValues, dirtyData: currentValues },
        }),
      );
      setLastFormIndex(step);

      if (lastFormIndex >= step) {
        setStep(step - 1);
      } else {
        setStep(lastFormIndex);
      }

      return;
    }

    handleClose?.();
  };

  return (
    <FormContainer justifyContent="space-between">
      <FormContent $overflowVisible={overflowVisible}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h5">{title}</Typography>
          <StepTag currentStep={step} maxSteps={maxSteps} />
        </Stack>
        {children}
      </FormContent>
      <FormWizardSubmit
        closeButtonLabel={closeButtonLabel}
        submitButtonOnly={submitButtonOnly}
        onClose={_handleClose}
        displayButtonsAsFullwidth={false}
        skipable={skipable}
        skipSteps={skipSteps}
        isLastStep={maxSteps === step + 1}
        submitButtonLabel={submitButtonLabel}
        formHasToBeDirty={formHasToBeDirty}
      />
    </FormContainer>
  );
};
